import React from 'react';
import {useTranslation} from 'react-i18next';

import {Button} from '@edna/components';
import {Icon, Stack} from '@edna/components/primitives';
import {TSpacingSize} from '@edna/components/types';

import {NoShrinkImage, Text} from 'src/components/primitives';
import {LogoImage} from 'src/images';

import * as S from './style';

type TProps = {
  title: string | React.ReactNode;
  children: React.ReactNode;
  leftImage?: React.ReactNode;
  rightImage?: React.ReactNode;
  badgeTitle?: React.ReactNode;
  onClose?: TEmptyFunction;
};

const NewsImage = React.memo<{image: TSvgComponentType}>(({image}) => (
  <NoShrinkImage as={image} $maxWidth="241px" />
));

const NewsBadgePanel = React.memo<{onClose?: TAnyFunction; badgeTitle?: string}>(
  ({badgeTitle, onClose}) => {
    const {t} = useTranslation();

    return (
      <Stack direction="ROW" justify="space-between" isFullWidth>
        <S.Badge>
          <Icon as={LogoImage} size="16px" />
          <span>{badgeTitle ?? t('News:ednaNews')}</span>
        </S.Badge>
        {onClose && <S.CloseIcon onClick={onClose} />}
      </Stack>
    );
  },
);

const NewsTitle = React.memo<{children: React.ReactNode}>(({children}) => (
  <Text size="XL" weight={500} color="black">
    {children}
  </Text>
));

const NewsBody = React.memo<{
  title: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
  onClose?: TAnyFunction;
}>(({title, action, children, onClose}) => {
  const {t} = useTranslation();

  return (
    <Stack size="5">
      <NewsTitle>{title}</NewsTitle>
      <S.Content>{children}</S.Content>
      {onClose && (
        <Button size="S" onClick={onClose}>
          {t('Common:button.understand')}
        </Button>
      )}
      {action}
    </Stack>
  );
});

const VerticalNewsContainer = React.memo<{children: React.ReactNode; size?: TSpacingSize}>(
  ({children, size = '4'}) => <Stack size={size}>{children}</Stack>,
);

const HorizontalNewsContainer = React.memo<{children: React.ReactNode; size?: TSpacingSize}>(
  ({children, size = '10'}) => (
    <Stack direction="ROW" size={size} justify="space-between" align="stretch">
      {children}
    </Stack>
  ),
);

export {
  NewsImage,
  NewsBadgePanel,
  NewsBody,
  VerticalNewsContainer,
  HorizontalNewsContainer,
  NewsTitle,
};
export type {TProps};
